import { useEffect, useState } from 'react'
import {
  Button,
  CheckIcon,
  ContextHeading,
  Field,
  Label,
  PageHeading,
  Paragraph,
  Popup,
  RadioGroup,
  Select,
} from '@quickmd-llc/design-system'
import { usePostPatientsTargetPatientIdMerge } from '@/api/generated/patient/patient'
import { ADMIN_EVENT_NAMES, tracking } from '@/lib/segment/tracking'
import { type RestrictionStatus } from '@/types/patient'
import { MergeAccountCard } from './MergeAccountCard'
import { useMergeAccounts } from '../context/useMergeAccounts'

const restrictionOptions: Record<Exclude<RestrictionStatus, 'merged'>, string> = {
  none: 'Compliant',
  drugscreenrequired: 'Must complete drug screen',
  inpersonvisitrequired: 'Must complete in-person visit',
  onhold: 'On hold',
}

type Props = {
  onClose: () => void
}

export function MergeAccountsPopup({ onClose }: Props) {
  const { reset } = useMergeAccounts()
  const [targetPatientId, setTargetPatientId] = useState<string>()
  const [targetRestrictionStatus, setTargetRestrictionStatus] = useState<RestrictionStatus>('none')
  const [showSuccess, setShowSuccess] = useState<boolean>(false)

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        reset()
        onClose()
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [onClose, reset, showSuccess])

  const { selectedPatients } = useMergeAccounts()
  const { mutate, isPending } = usePostPatientsTargetPatientIdMerge({
    mutation: {
      onSuccess: () => {
        tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_MERGE_ACCOUNT_DETAILS_CONFIRM_MERGE)

        setShowSuccess(true)
      },
      onError: (error) => {
        console.error('Error', error)
      },
    },
  })

  function handleChangeRadioGroup(value: string) {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_MERGE_ACCOUNT_DETAILS_SELECT_MAIN_ACCOUNT)

    setTargetPatientId(value)
  }

  function handleChangeRestrictionStatus(value: RestrictionStatus) {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_MERGE_ACCOUNT_DETAILS_SELECT_STATUS)

    setTargetRestrictionStatus(value)
  }

  function handleConfirmMerge() {
    const sourcePatientIds = selectedPatients
      .filter((patient) => patient.PatientId !== targetPatientId)
      .map((patient) => patient.PatientId)

    mutate({
      // @ts-expect-error TS2351 TEMP ERROR SUPPRESSION: CRA -> VITE
      targetPatientId,
      data: {
        SourcePatientIds: sourcePatientIds,
        TargetPatientAccountRestrictionStatus: targetRestrictionStatus,
      },
    })
  }

  function handleCancel() {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_MERGE_ACCOUNT_DETAILS_CANCEL)

    onClose()
  }

  function handleClose() {
    onClose()
  }

  if (showSuccess) {
    return (
      <Popup
        isOpen
        lockOpen
        body={
          <div className='grid justify-center gap-sp-4'>
            <div className='flex h-[96px] w-[96px] items-center justify-center justify-self-center rounded-full bg-primary-seafoam'>
              <CheckIcon iconSize='xxl' className='justify-center text-text-inverse' />
            </div>

            <PageHeading>Merge complete!</PageHeading>
          </div>
        }
        onClose={() => handleClose()}
      />
    )
  }

  return (
    <Popup
      isOpen
      header={
        <div>
          <ContextHeading>Merge account details</ContextHeading>

          <Paragraph size='sm'>
            Select the main account before merging {selectedPatients.length} accounts. Use Unmerge in the patient chart
            if you need to undo this action.
          </Paragraph>
        </div>
      }
      body={
        <div className='grid gap-sp-2'>
          <Field>
            <Label>Select main account</Label>

            <RadioGroup
              onChange={(value) => handleChangeRadioGroup(value)}
              className='max-h-[400px] divide-y divide-border-dark overflow-y-auto rounded-lg border border-border-dark'>
              {selectedPatients.map((patient) => (
                <MergeAccountCard key={patient.PatientId} patient={patient} />
              ))}
            </RadioGroup>
          </Field>

          <Field>
            <Label>Select status for main account</Label>

            <Select
              placeholder='Select status'
              value={targetRestrictionStatus}
              onChange={({ target }) => handleChangeRestrictionStatus(target.value as RestrictionStatus)}>
              {Object.entries(restrictionOptions).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      }
      footer={
        <div className='flex justify-end gap-sp-1'>
          <Button onClick={() => handleCancel()} disabled={isPending} priority='secondary'>
            Cancel
          </Button>

          <Button onClick={() => handleConfirmMerge()} loading={isPending}>
            Confirm merge
          </Button>
        </div>
      }
      showCloseIcon
      onClose={() => handleCancel()}
    />
  )
}
